import { useState } from "react"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { MousePointer, Users, Eye, ShoppingCart, DollarSign, TrendingUp, Brain, ArrowRight, Loader2 } from "lucide-react";
import axiosInstance from "../../../API/axiosInstance"
import { toast } from 'react-toastify'

export function Performance({ projectName, onBack, onUpdatePlan, adId, aiCmoId }) {
    const [performanceData, setPerformanceData] = useState({
        ctr: "",
        totalClicks: "",
        lpv: "",
        purchases: "",
        aov: "",
        roas: "",
    })
    const [isAnalyzing, setIsAnalyzing] = useState(false)
    const [isIterating, setIsIterating] = useState(false)
    const [analysisResult, setAnalysisResult] = useState(null)

    const handleAnalyze = async () => {
        setIsAnalyzing(true)
        try {
            const response = await axiosInstance.post(`/cmo/analyze-performance?adId=${adId}`, performanceData)
            if (response.data?.data?.report) {
                setAnalysisResult(response.data.data.report)
            }
        } catch (error) {
            console.error("Failed to analyze performance:", error)
            toast.error("Failed to analyze performance. Please try again.")
        } finally {
            setIsAnalyzing(false)
        }
    }

    const handleIterate = async () => {
        if (!analysisResult?._id) return
        setIsIterating(true)
        try {
            const response = await axiosInstance.post("/cmo/iterate-concept-from-performance", {
                aiCmoId: aiCmoId,
                reportId: analysisResult._id
            })
            if (response.data?.data?.newConcept) {
                toast.success("New concept generated successfully!")
                onUpdatePlan(response.data.data.newConcept)
            }
        } catch (error) {
            console.error("Failed to iterate concept:", error)
            toast.error("Failed to generate iterative concept. Please try again.")
        } finally {
            setIsIterating(false)
        }
    }

    return (
        <div className="flex-1 min-w-0 overflow-y-auto py-6 px-4 sm:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto space-y-6 pb-12">
                <div className="space-y-1">
                    {projectName && (
                        <p className="text-xs font-medium uppercase tracking-wider text-orange-500 dark:text-orange-400">
                            {projectName}
                        </p>
                    )}
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Performance Analysis
                    </h1>
                    <p className="text-sm text-gray-700 dark:text-gray-400">
                        Enter your ad performance metrics to get AI-powered insights and improvements.
                    </p>
                </div>

                <div className="p-5 rounded-xl border bg-white border-gray-200 dark:bg-white/5 dark:border-white/10">
                    <p className="text-xs mb-4 text-gray-400 dark:text-gray-500">
                        Use numbers from your campaign dashboard
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400">
                                Click-Through Rate (CTR)
                            </label>
                            <div className="relative">
                                <MousePointer className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <Input
                                    type="text"
                                    placeholder="e.g., 2.5%"
                                    value={performanceData.ctr}
                                    onChange={(e) => setPerformanceData((prev) => ({ ...prev, ctr: e.target.value }))}
                                    className="pl-10 h-12 rounded-xl transition-all bg-white border-gray-200 text-black placeholder:text-gray-400 focus:bg-gray-50 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500 dark:focus:bg-white/10"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400">
                                Total Clicks
                            </label>
                            <div className="relative">
                                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <Input
                                    type="text"
                                    placeholder="e.g., 1,500"
                                    value={performanceData.totalClicks}
                                    onChange={(e) => setPerformanceData((prev) => ({ ...prev, totalClicks: e.target.value }))}
                                    className="pl-10 h-12 rounded-xl transition-all bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 focus:bg-gray-50 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500 dark:focus:bg-white/10"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400">
                                Landing Page Views (LPV)
                            </label>
                            <div className="relative">
                                <Eye className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <Input
                                    type="text"
                                    placeholder="e.g., 2,000"
                                    value={performanceData.lpv}
                                    onChange={(e) => setPerformanceData((prev) => ({ ...prev, lpv: e.target.value }))}
                                    className="pl-10 h-12 rounded-xl transition-all bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 focus:bg-gray-50 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500 dark:focus:bg-white/10"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400">
                                Purchases
                            </label>
                            <div className="relative">
                                <ShoppingCart className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <Input
                                    type="text"
                                    placeholder="e.g., 45"
                                    value={performanceData.purchases}
                                    onChange={(e) => setPerformanceData((prev) => ({ ...prev, purchases: e.target.value }))}
                                    className="pl-10 h-12 rounded-xl transition-all bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 focus:bg-gray-50 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500 dark:focus:bg-white/10"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400">
                                Average Order Value (AOV)
                            </label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <Input
                                    type="text"
                                    placeholder="e.g., $85"
                                    value={performanceData.aov}
                                    onChange={(e) => setPerformanceData((prev) => ({ ...prev, aov: e.target.value }))}
                                    className="pl-10 h-12 rounded-xl transition-all bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 focus:bg-gray-50 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500 dark:focus:bg-white/10"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400">
                                Return on Ad Spend (ROAS)
                            </label>
                            <div className="relative">
                                <TrendingUp className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <Input
                                    type="text"
                                    placeholder="e.g., 2.5x"
                                    value={performanceData.roas}
                                    onChange={(e) => setPerformanceData((prev) => ({ ...prev, roas: e.target.value }))}
                                    className="pl-10 h-11 text-base rounded-xl transition-all bg-white border-gray-200 text-black placeholder:text-gray-400 focus:bg-gray-50 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500 dark:focus:bg-white/10"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <Button
                            onClick={handleAnalyze}
                            disabled={isAnalyzing}
                            className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium transition-all shadow-lg active:scale-95 disabled:opacity-50"
                        >
                            {isAnalyzing ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Analyzing...
                                </>
                            ) : (
                                <>
                                    Analyze Performance
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {analysisResult && (
                    <div className="p-6 rounded-2xl border transition-all animate-in fade-in slide-in-from-bottom-4 duration-500 bg-gradient-to-br from-orange-50 to-white border-orange-200 dark:from-orange-500/10 dark:to-transparent dark:border-orange-500/20">
                        <div className="flex items-start gap-4">
                            <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-500/20">
                                <Brain className="w-6 h-6 text-orange-500" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                    AI Performance Analysis
                                </h2>

                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wider mb-1 text-orange-600 dark:text-orange-400">
                                            What's happening
                                        </p>
                                        <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-300">
                                            {analysisResult.aiAnalysis.whatsHappening}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wider mb-1 text-orange-600 dark:text-orange-400">
                                            Why this is happening
                                        </p>
                                        <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-300">
                                            {analysisResult.aiAnalysis.whyThisIsHappening}
                                        </p>
                                    </div>

                                    <div className="p-4 rounded-xl bg-white border border-orange-100 dark:bg-white/5 dark:border-white/10">
                                        <p className="text-xs font-medium uppercase tracking-wider mb-2 text-orange-600 dark:text-orange-400">
                                            What to try next
                                        </p>
                                        <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-300">
                                            {analysisResult.aiAnalysis.whatToTryNext}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-8 pt-6 border-t border-orange-200 dark:border-white/10">
                                    <h3 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">
                                        Ready to improve this campaign?
                                    </h3>
                                    <Button
                                        onClick={handleIterate}
                                        disabled={isIterating}
                                        className="h-12 px-6 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-medium transition-all shadow-md active:scale-95 disabled:opacity-50"
                                    >
                                        {isIterating ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                Update my plan
                                                <ArrowRight className="ml-2 w-4 h-4" />
                                            </>
                                        )}
                                    </Button>
                                    <p className="text-[11px] mt-3 opacity-60 text-gray-400 dark:text-gray-500">
                                        This will generate a new ad concept based on these performance insights.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
